import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { FormikProps } from "formik";

import { OnClickResendOTPParams } from "@vahak/core/dist/components/auth/useAuth";
import { GA4EventNames, WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import OTPInput from "@vahak/core-ui/dist/components/OTPInput";

import { LoginFormFieldsTypes, loginFormFieldNames } from "../../login-form-new/common/contant";
import { getTimeRemaining } from "../../login-form-new/login-form-fields/otp-field/OtpField";
import {
    LOGIN_OTP_LENGTH,
    useOtpAutoSubmit,
    useResendOtpTimer,
    useSmsOtpAutofill
} from "../../login-form-new/hooks/auth";
import useCompactLoadPostingTranslation from "../compact-load-posting/hooks/useCompactLoadPostingTranslation";
import { TrackEventHandler } from "../../../types/events";
import { useCompactSheetRefAutoFocus } from "../hooks/useCompactSheetAutoFocus";
import { createCompactSheetInputFocusHandler } from "../methods/homepageV2CompactSheet";

import Styles from "./CompactOtpField.module.scss";

interface CompactOtpFieldProps {
    formik: FormikProps<LoginFormFieldsTypes>;
    onClickResendOtp: (params: OnClickResendOTPParams) => void;
    onClickEditNumber?: () => void;
    otpSentToKey?: string;
    compactBoxes?: boolean;
    trackEvent?: TrackEventHandler;
}

const RESEND_BUFFER_SECONDS = 30;
const handleAuthFocus = createCompactSheetInputFocusHandler("auth");

function formatOtpDigits(otp?: string) {
    return (otp ?? "").replace(/\D/g, "");
}

function formatPhoneDisplay(phone: string) {
    const digits = phone.replace(/\D/g, "");
    if (digits.length <= 5) {
        return digits;
    }
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
}

const parseOtpValue = (value: string) => value.replace(/\D/g, "").slice(0, LOGIN_OTP_LENGTH);

const CompactOtpField = ({
    formik,
    onClickResendOtp,
    onClickEditNumber,
    otpSentToKey = "load-form:login.otpSentTo",
    compactBoxes = false,
    trackEvent
}: CompactOtpFieldProps) => {
    const { t } = useCompactLoadPostingTranslation();
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    const otpWrapperRef = useRef<HTMLDivElement>(null);
    const wasSubmittingRef = useRef(false);
    const otpDigitCountRef = useRef(0);

    const { timeRemaining, restartCountdown } = useResendOtpTimer(RESEND_BUFFER_SECONDS);

    const { resetAutoSubmit } = useOtpAutoSubmit({
        formik,
        onSubmit: () => formik.submitForm()
    });

    const otpDigitCount = formatOtpDigits(formik.values.otp).length;
    const showOtpError = Boolean(formik.errors.otp && otpDigitCount === LOGIN_OTP_LENGTH);
    const formattedPhone = formatPhoneDisplay(String(formik.values.phoneNumber));

    useCompactSheetRefAutoFocus(otpWrapperRef, 'input:not([type="hidden"])', {
        scrollIntoView: false
    });

    useEffect(() => {
        const wrapper = otpWrapperRef.current;
        if (!wrapper) {
            return;
        }

        const onFocusIn = (event: FocusEvent) => {
            if (!(event.target instanceof HTMLInputElement)) {
                return;
            }

            handleAuthFocus(event);
        };

        wrapper.addEventListener("focusin", onFocusIn);

        return () => {
            wrapper.removeEventListener("focusin", onFocusIn);
        };
    }, []);

    const applyOtpValue = (value: string) => {
        if (formik.errors.otp) {
            formik.setFieldError(loginFormFieldNames.otp, undefined);
        }
        formik.setFieldValue(loginFormFieldNames.otp, parseOtpValue(value));
    };

    const { startListening: startSmsOtpAutofill } = useSmsOtpAutofill({
        onOtp: (code) => {
            otpWrapperRef.current?.querySelector("input")?.blur();
            applyOtpValue(code);
        }
    });

    useEffect(() => {
        otpDigitCountRef.current = otpDigitCount;
    }, [otpDigitCount]);

    useEffect(() => {
        const inputs = otpWrapperRef.current?.querySelectorAll("input");
        inputs?.forEach((input) => input.setAttribute("autocomplete", "one-time-code"));
    }, []);

    useEffect(() => {
        const submitJustFinished = wasSubmittingRef.current && !formik.isSubmitting;

        if (submitJustFinished && formik.errors.otp) {
            otpWrapperRef.current?.querySelectorAll("input").forEach((input) => input.blur());
        }

        wasSubmittingRef.current = formik.isSubmitting;
    }, [formik.errors.otp, formik.isSubmitting]);

    const onChangeOTP = (value: string) => {
        resetAutoSubmit();

        const nextDigitCount = value.replace(/\D/g, "").length;
        if (nextDigitCount > otpDigitCountRef.current) {
            trackEvent?.(GA4EventNames.WEB_V2.otp_entered, {
                source_screen: WEB_V2_SOURCE_SCREENS.ONB_FORM,
                otp_length: nextDigitCount,
                Number: formik.values.phoneNumber
            });
        }
        otpDigitCountRef.current = nextDigitCount;

        applyOtpValue(value);
    };

    const showResendCountdown = timeRemaining > 0;
    const disableResendClick = showResendCountdown || isResendingOtp;
    const { minutes, seconds } = getTimeRemaining(timeRemaining);
    const resendLabel = `${Number(minutes)}:${seconds}`;

    const handleResend = () => {
        if (disableResendClick) {
            return;
        }

        resetAutoSubmit();
        restartCountdown();
        setIsResendingOtp(true);
        startSmsOtpAutofill();

        if (formik.errors.otp) {
            formik.setFieldError(loginFormFieldNames.otp, undefined);
        }

        let pendingChannels = 2;
        const onChannelSettled = () => {
            pendingChannels -= 1;
            if (pendingChannels <= 0) {
                setIsResendingOtp(false);
            }
        };

        onClickResendOtp({
            onSuccessCallback: () => {},
            onSettledCallback: onChannelSettled
        });
        onClickResendOtp({
            isWhatsApp: true,
            onSuccessCallback: () => {},
            onSettledCallback: onChannelSettled
        });
    };

    return (
        <div className={classnames(Styles.field, compactBoxes && Styles.fieldCompact)}>
            {compactBoxes ? (
                <div className={Styles.numberChip}>
                    <span className={Styles.numberChipValue}>+91 {formattedPhone}</span>
                    <button type="button" className={Styles.changeNumberBtn} onClick={onClickEditNumber}>
                        {t("load-form:login.changeNumberShort")}
                    </button>
                </div>
            ) : (
                <>
                    <p className={Styles.sentTo}>{t(otpSentToKey, { phone: formattedPhone })}</p>
                    <button type="button" className={Styles.changeNumberBtn} onClick={onClickEditNumber}>
                        {t("load-form:login.changeNumber")}
                    </button>
                </>
            )}

            <p className={Styles.label}>{t("load-form:login.otpLabel")}</p>

            <OTPInput
                ref={otpWrapperRef}
                id="compact-login-otp"
                length={LOGIN_OTP_LENGTH}
                value={formatOtpDigits(formik.values.otp)}
                onChange={onChangeOTP}
                inputClassName={classnames(Styles.otpDigit, showOtpError && Styles.otpDigitError)}
                containerClassName={Styles.otpContainer}
            />

            <p className={Styles.resendRow}>
                {showResendCountdown ? (
                    <>
                        {t("load-form:login.resendIn")} <span className={Styles.resendTimer}>{resendLabel}</span>
                    </>
                ) : (
                    <button type="button" className={Styles.resendBtn} onClick={handleResend} disabled={isResendingOtp}>
                        {t("load-form:login.resendOtp")}
                    </button>
                )}
            </p>
        </div>
    );
};

export default CompactOtpField;
